/**
 * Asset Manager for White Label Solution
 * 
 * This utility facilitates the management of assets (logos, images) 
 * for white labeling purposes, making it easy to swap out visual elements.
 */

import brandingConfig from '../config/brandingConfig';

/**
 * Get the correct asset path based on the configuration
 * This handles both relative and absolute paths
 * 
 * @param {string} assetKey - The key in brandingConfig.logos or a direct path
 * @param {boolean} isLogo - Whether this asset is a logo (from brandingConfig.logos)
 * @return {string} The full path to the asset
 */
export const getAssetPath = (assetKey, isLogo = true) => {
  let assetPath;
  
  // If this is a logo, get it from the branding config
  if (isLogo) {
    assetPath = brandingConfig.logos[assetKey];
  } else {
    assetPath = assetKey;
  }
  
  if (!assetPath) {
    console.warn(`Asset not found: ${assetKey}`);
    return null;
  }
  
  // Handle absolute URLs
  if (assetPath.startsWith('http')) {
    return assetPath;
  }
  
  // Handle public assets
  if (assetPath.startsWith('/')) {
    return process.env.PUBLIC_URL + assetPath;
  }

  // Relative paths are resolved from the public/ directory too. A dynamic
  // require() here would make webpack bundle the entire src/ tree as a
  // context module (including unrelated, possibly broken files), so we
  // deliberately avoid it in favor of the same PUBLIC_URL resolution used
  // above.
  const normalizedPath = assetPath.startsWith('assets/')
    ? assetPath
    : `assets/${assetPath.split('/').pop()}`;
  return `${process.env.PUBLIC_URL}/${normalizedPath}`;
};

/**
 * Asset component props creator
 * Creates a props object for any React component that displays an image
 * 
 * @param {string} assetKey - The key in brandingConfig.logos or a direct path
 * @param {string} altText - The alt text for the image
 * @param {boolean} isLogo - Whether this asset is a logo
 * @return {object} Props object with src and alt
 */
export const createAssetProps = (assetKey, altText = '', isLogo = true) => {
  return {
    src: getAssetPath(assetKey, isLogo),
    alt: altText || (isLogo ? `${brandingConfig.appName} ${assetKey}` : assetKey)
  };
};

/**
 * Get a favicon URL
 * 
 * @return {string} URL to the favicon
 */
export const getFaviconUrl = () => {
  return getAssetPath('favicon', true);
};

/**
 * Get a logo URL with the specified size
 * 
 * @param {string} logoType - The logo type ('primary', 'icon', etc.)
 * @param {string} size - The desired size ('small', 'medium', 'large')
 * @return {object} Props object with src, alt, and size-appropriate style
 */
export const getLogoWithSize = (logoType = 'primary', size = 'medium') => {
  const sizeMap = {
    small: { height: 24 },
    medium: { height: 32 },
    large: { height: 48 },
    xlarge: { height: 64 }
  };
  
  return {
    ...createAssetProps(logoType),
    style: sizeMap[size] || sizeMap.medium
  };
};

export default {
  getAssetPath,
  createAssetProps,
  getFaviconUrl,
  getLogoWithSize
};